import { type FunctionComponent, useEffect, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { type CloudinaryOptions } from '../../../helpers/cloudinary-helper/cloudinary-helper';
import { type Variation } from '../../../helpers/images/images.helper';
import { CloudinaryImage, type CloudinaryImageProps } from '../../images/cloudinary-image/cloudinary-image.component';
import { zoomImage } from './zoomable-image.css';

const INITIAL_ZOOM = 1;
const ZOOM_SCALE = 1.75;

export const ZoomableImage: FunctionComponent<CloudinaryImageProps> = (props) => {
	const [hasMounted, setHasMounted] = useState(false);

	const containerRef = useRef<HTMLInputElement>(null);

	/**
	 * We want to display the low res image on the first render (server side)
	 * and then after it mounts replace it with the high res one
	 */
	useEffect(() => {
		setHasMounted(true);
	}, [props.publicID]);

	const cloudinaryOptions: CloudinaryOptions = {
		...props.options,
		crop: 'pad',
		dpr: 2
	};

	const cloudinaryVariations: Variation[] | undefined = props?.variations?.map((variation) => {
		return {
			mediaCondition: variation.mediaCondition,
			options: { ...variation.options, crop: 'pad' }
		};
	});

	return (
		<div ref={containerRef}>
			<TransformWrapper
				limitToBounds={true}
				maxScale={ZOOM_SCALE}
				wheel={{ disabled: true }}
				doubleClick={{ disabled: true }}
				panning={{ disabled: true }}>
				{({ setTransform }) => {
					const onMouseMove = (event) => {
						// It should always have a ref at this point.
						if (containerRef.current) {
							const boundingRect = containerRef.current.getBoundingClientRect();
							setTransform(-(event.clientX - boundingRect.left), -(event.clientY - boundingRect.top), ZOOM_SCALE, 0);
						}
					};
					const onMouseLeave = () => {
						setTransform(0, 0, INITIAL_ZOOM, 0);
					};
					return (
						<div
							onMouseLeave={onMouseLeave}
							onMouseMove={onMouseMove}
							role="button"
							tabIndex={0}
							className={`flex flex-column items-center ${zoomImage}`}>
							<TransformComponent>
								<CloudinaryImage
									{...props}
									options={hasMounted ? cloudinaryOptions : props.options}
									useDimensions={false}
									variations={cloudinaryVariations}
									limitHeight={true}
									className={`w-auto self-center ${props.className}`}
								/>
							</TransformComponent>
						</div>
					);
				}}
			</TransformWrapper>
		</div>
	);
};
